import { useEffect, useRef, useState } from "react";
import { Input, Tag } from "antd";
import type { InputRef } from "antd";

export type RecipientField = "to" | "cc" | "bcc";
export type RecipientMap = Record<RecipientField, string[]>;

const FIELDS: RecipientField[] = ["to", "cc", "bcc"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const splitInput = (raw: string) =>
  raw
    .split(/[,;\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);

const dedupeAcrossFields = (next: RecipientMap): RecipientMap => {
  const seen = new Set<string>();
  const out: RecipientMap = { to: [], cc: [], bcc: [] };
  FIELDS.forEach((field) => {
    next[field].forEach((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out[field].push(email);
    });
  });
  return out;
};

function useHorizontalWheel<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.deltaY || el.scrollWidth <= el.clientWidth) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  return ref;
}

interface RecipientRowProps {
  field: RecipientField;
  values: string[];
  editable: boolean;
  draft: string;
  isDropTarget: boolean;
  invalid: boolean;
  onDraftChange: (value: string) => void;
  onCommitDraft: () => void;
  onRemove: (email: string) => void;
  onDragStart: (email: string) => void;
  onDragEnd: () => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
}

function RecipientRow({
  field,
  values,
  editable,
  draft,
  isDropTarget,
  invalid,
  onDraftChange,
  onCommitDraft,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: Readonly<RecipientRowProps>) {
  const scrollRef = useHorizontalWheel<HTMLDivElement>();
  const inputRef = useRef<InputRef>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "," || event.key === ";" || event.key === "Tab") {
      if (!draft.trim()) return;
      event.preventDefault();
      onCommitDraft();
      return;
    }
    if (event.key === "Backspace" && !draft && values.length) {
      event.preventDefault();
      onRemove(values[values.length - 1]);
    }
  };

  return (
    <div
      className={[
        "flex min-w-0 items-center rounded transition-colors",
        isDropTarget ? "bg-indigo-100 ring-1 ring-indigo-400" : "",
        invalid ? "ring-1 ring-red-400" : "",
      ].join(" ")}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={() => editable && inputRef.current?.focus()}
    >
      <span className="w-9 shrink-0 font-mono text-xs text-nowrap font-semibold opacity-70">{field}:</span>

      <div
        ref={scrollRef}
        className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-x-auto whitespace-nowrap py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {values.map((email) => (
          <Tag
            key={email}
            draggable={editable}
            onDragStart={() => onDragStart(email)}
            onDragEnd={onDragEnd}
            closable={editable}
            onClose={(event) => {
              event.preventDefault();
              onRemove(email);
            }}
            className={["m-0 flex-none", editable ? "cursor-grab active:cursor-grabbing" : ""].join(" ")}
          >
            {email}
          </Tag>
        ))}

        {editable && (
          <Input
            ref={inputRef}
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onCommitDraft}
            onPaste={(event) => {
              const text = event.clipboardData.getData("text");
              if (!/[,;\s]/.test(text)) return;
              event.preventDefault();
              onDraftChange(text);
            }}
            variant="borderless"
            size="small"
            placeholder={values.length ? "" : "Add address"}
            className="w-40 flex-none px-1 text-xs"
          />
        )}
      </div>
    </div>
  );
}

interface RecipientEditorProps {
  value: RecipientMap;
  editable: boolean;
  onChange: (next: RecipientMap) => void;
}

export default function RecipientEditor({ value, editable, onChange }: Readonly<RecipientEditorProps>) {
  const dragged = useRef<{ field: RecipientField; email: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<RecipientField | null>(null);
  const [drafts, setDrafts] = useState<Record<RecipientField, string>>({ to: "", cc: "", bcc: "" });
  const [invalid, setInvalid] = useState<RecipientField | null>(null);

  const commit = (next: RecipientMap) => onChange(dedupeAcrossFields(next));

  const commitDraft = (field: RecipientField) => {
    const parts = splitInput(drafts[field]);
    if (!parts.length) return;
    const valid = parts.filter((part) => EMAIL_RE.test(part));
    const rejected = parts.filter((part) => !EMAIL_RE.test(part));
    if (valid.length) commit({ ...value, [field]: [...value[field], ...valid] });
    setDrafts((prev) => ({ ...prev, [field]: rejected.join(", ") }));
    setInvalid(rejected.length ? field : null);
  };

  const handleDragOver = (field: RecipientField) => (event: React.DragEvent) => {
    const source = dragged.current;
    if (!source || source.field === field) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (dropTarget !== field) setDropTarget(field);
  };

  const handleDrop = (field: RecipientField) => (event: React.DragEvent) => {
    event.preventDefault();
    const source = dragged.current;
    dragged.current = null;
    setDropTarget(null);
    if (!source || source.field === field) return;
    commit({
      ...value,
      [source.field]: value[source.field].filter((email) => email !== source.email),
      [field]: [...value[field], source.email],
    });
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      {FIELDS.map((field) => {
        if (!editable && !value[field].length) return null;
        return (
          <RecipientRow
            key={field}
            field={field}
            values={value[field]}
            editable={editable}
            draft={drafts[field]}
            isDropTarget={dropTarget === field}
            invalid={invalid === field}
            onDraftChange={(next) => {
              setDrafts((prev) => ({ ...prev, [field]: next }));
              if (invalid === field) setInvalid(null);
            }}
            onCommitDraft={() => commitDraft(field)}
            onRemove={(email) => commit({ ...value, [field]: value[field].filter((item) => item !== email) })}
            onDragStart={(email) => {
              dragged.current = { field, email };
            }}
            onDragEnd={() => {
              dragged.current = null;
              setDropTarget(null);
            }}
            onDragOver={handleDragOver(field)}
            onDrop={handleDrop(field)}
          />
        );
      })}
    </div>
  );
}
